"use client";

import { AreaSeries, LastPointMarker, LightweightChart } from "@/components/charts/lightweight-chart";
import { CryptoGuess } from "@/event-core/types";
import { useEventConnect } from "@/store/eventConnect";
import { formatLocalDate } from "@/utils/formatLocalDate";
import { resolveUnitPrice } from "@/utils/price";
import { BigMath } from "@yn/utils";
import { ISeriesApi, UTCTimestamp } from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { fetchGetEventChartData } from "./api";

/**
 * 价格走势图组件
 * 展示事件的价格数据
 */
function PriceChart( { eventNo, guessNo, guessList }: { eventNo: string; guessNo: string; guessList: CryptoGuess[] } ) {
    const [ series, setSeries ] = useState<ISeriesApi<"Area"> | null>( null );
    const [ chartData, setChartData ] = useState<Array<{ time: UTCTimestamp; value: number }>>( [] );
    const [ appendData, setAppendData ] = useState<{ time: UTCTimestamp; value: number } | undefined>( undefined );
    const chartDataRef = useRef<Array<{ time: UTCTimestamp; value: number }>>( [] );
    const onMarketPriceChange = useEventConnect( state => state.onMarketPriceChange );

    useEffect( () => {
        if ( eventNo && guessNo && guessList.length > 0 ) {
            // 找到当前 guessNo 在 guessList 中的位置
            const currentIndex = guessList.findIndex( ( g ) => g.guessNo === guessNo );
            if ( currentIndex === -1 ) return;

            // 提取从 0 到当前索引的所有 guessNo（包括自己）
            const guessNoList = guessList.slice( 0, currentIndex + 1 );

            setAppendData( undefined );

            fetchGetEventChartData( eventNo, guessNoList ).then( ( data ) => {
                const formattedData = data.map( ( item ) => ( {
                    time: item.timestamp as UTCTimestamp,
                    value: Number( resolveUnitPrice( +item.yesPrice ) ),
                } ) );
                chartDataRef.current = formattedData;
                setChartData( formattedData );
            } );
        }
    }, [ eventNo, guessList, guessNo ] );

    useEffect( () => {
        // 监听价格变化，更新图表数据
        const unsubscribe = onMarketPriceChange( ( event ) => {
            // 只监听当前竞猜的价格变化
            if ( event.guessNo !== guessNo ) return;

            // 构建图表数据格式（timestamp 需要转换为秒级时间戳）
            const time = Math.floor( event.timestamp / 1000 ) as UTCTimestamp;
            const value = Number( resolveUnitPrice( event.yesPrice ) );

            setAppendData( ( prev ) => {
                // 检查时间戳是否比上一个点大（Lightweight Chart 要求严格递增）
                const currentChartData = chartDataRef.current;
                const lastPoint = prev
                    || ( currentChartData.length > 0 ? currentChartData[currentChartData.length - 1] : null );
                if ( lastPoint && time <= lastPoint.time ) {
                    return prev;
                }

                const newDataPoint = { time, value };

                // 同时更新 series（如果已初始化）
                if ( series ) {
                    series.update( newDataPoint );
                }

                return newDataPoint;
            } );
        } );

        return () => unsubscribe();
    }, [ guessNo, onMarketPriceChange, series ] );
    const latestPoint = appendData || chartData[chartData.length - 1];
    return (
        <LightweightChart
            options={{
                rightPriceScale: {
                    visible: true,
                    alignLabels: false,
                    borderVisible: false,
                    entireTextOnly: true,
                    scaleMargins: { top: 0.2, bottom: 0.04 },
                },
                localization: {
                    priceFormatter: ( price: number ) => {
                        return `${BigMath.times( price, 1, 0 )}%`;
                    },
                    // 自定义十字光标处的时间格式
                    timeFormatter: ( time: number ) => {
                        return formatLocalDate( time, "HH:mm:ss" );
                    },
                },
            }}
        >
            <AreaSeries
                ref={setSeries}
                data={chartData}
                appendData={appendData}
                options={{ lineColor: "#086DE0", lineWidth: 2, crosshairMarkerVisible: true, title: "" }}
            />

            {/* 追加末端脉冲原点 */}
            {latestPoint && <LastPointMarker series={series} data={latestPoint} color="#086DE0" />}
        </LightweightChart>
    );
}

export default PriceChart;
